import {
  faEye,
  faInfoCircle,
  faPaperclip,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import Loading from 'components/loading/Loading';
import DialogModal from 'components/modals/DialogModal';
import useApi from 'helpers/api';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

const TicketAttachments = forwardRef(
  ({ ticketId, ticketStatus, refreshLogs, onCountChange }, ref) => {
    const api = useApi();
    const { showSuccessNotification, showErrorNotification } =
      useNotification();

    const [attachments, setAttachments] = useState([]);
    const [selectedAttachment, setSelectedAttachment] = useState({});
    const [showDialogConfirm, setShowDialogConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const inputFile = useRef(null);

    const hasTicketId = ticketId != undefined;
    const isEmpty = hasTicketId && attachments && attachments.length === 0;

    useEffect(() => {
      getData();
    }, [ticketId]);

    useEffect(() => {
      if (selectedFile) {
        attachFile();
      }
    }, [selectedFile]);

    useEffect(() => {
      if (onCountChange && Array.isArray(attachments)) {
        onCountChange(attachments.length);
      }
    }, [attachments]);

    useImperativeHandle(ref, () => ({
      getFiles: () => getData()
    }));

    const getData = async () => {
      if (hasTicketId) {
        let response = await api.ticketFiles.list(ticketId);
        setAttachments(response);
      }
    };

    const attachFile = async () => {
      setLoading(true);
      if (selectedFile && hasTicketId) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const request = await api.ticketFiles.attach(formData, ticketId);
        if (!request.ok) {
          showErrorNotification(
            request.userMessage || 'Erro ao anexar arquivo'
          );
          setLoading(false);
          return;
        }

        getData();
        refreshLogs();
        setSelectedFile(null);
        showSuccessNotification('Arquivo anexado com sucesso');
      }
      setLoading(false);
    };

    const deleteFile = async response => {
      if (response && hasTicketId) {
        const request = await api.ticketFiles.delete(
          ticketId,
          selectedAttachment.id
        );

        if (!request.ok) {
          showErrorNotification(
            request.userMessage || 'Erro ao excluir arquivo'
          );
          return;
        }

        getData();
        refreshLogs();
        showSuccessNotification('Arquivo excluído com sucesso');

        handleCloseDialog();
      }
    };

    const handleDelete = item => {
      setSelectedAttachment(item);
      setShowDialogConfirm(true);
    };

    const handleCloseDialog = () => {
      setShowDialogConfirm(false);
    };

    const handleFileSelect = e => {
      setSelectedFile(e.target.files[0]);
    };

    const handleAttachClick = () => {
      inputFile.current.click();
    };

    const columns = [
      {
        accessorKey: 'fileName',
        header: 'Nome',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { fileName } = rowData.row.original;
          return (
            <div className="text-truncate" style={{ maxWidth: '250px' }}>
              {fileName}
            </div>
          );
        }
      },
      {
        accessorKey: 'contentType',
        header: 'Tipo do arquivo',
        meta: {
          headerProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'size',
        header: 'Tamanho (bytes)',
        meta: {
          headerProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'actions',
        header: 'Ações',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const item = rowData.row.original;
          return (
            <Flex justifyContent="center" alignItems="center">
              <a
                href={item.url}
                target="_blank"
                className="p-0 me-2 d-flex align-items-center"
              >
                <FontAwesomeIcon icon={faEye} size="md" />
              </a>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-danger"
                onClick={() => handleDelete(item)}
                disabled={ticketStatus?.deleted}
              >
                <FontAwesomeIcon icon={faTrash} size="sm" />
              </Button>
            </Flex>
          );
        }
      }
    ];

    const table = useAdvanceTable({
      data: attachments,
      columns
    });

    return (
      <>
        {loading && <Loading />}
        <DialogModal
          message="Tem certeza que deseja excluir este arquivo?"
          showModal={showDialogConfirm}
          handleClose={handleCloseDialog}
          response={deleteFile}
        />

        <input
          type="file"
          ref={inputFile}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <Flex justifyContent="between" alignItems={'center'} className="mb-4">
          <Flex alignItems="end">
            <h3 className="m-0">Anexos</h3>
            <OverlayTrigger
              placement="bottom-start"
              overlay={
                <Tooltip>
                  Máximo de Arquivos: 1 arquivo por vez. Tamanho máximo por
                  arquivo: 6MB. Arquivos Suportados: .png, .gif, .jpeg, .jpg,
                  .rtf, .pdf, .doc, .docx, .txt, .xls, .xlsx, .xml, .json, .zip,
                  .rar, .csv, .pptx, .ppt, .pfx.
                </Tooltip>
              }
            >
              <span className="ms-2" role="button">
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </Flex>

          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleAttachClick}
            disabled={
              ticketStatus === 'COMPLETED' ||
              ticketStatus === 'CLOSED' ||
              ticketStatus?.deleted
            }
          >
            <FontAwesomeIcon icon={faPaperclip} size="sm" className="me-2" />
            Anexar
          </Button>
        </Flex>

        {isEmpty ? (
          <EmptyList imgSize={250} />
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              table
              headerClassName="text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
          </AdvanceTableProvider>
        )}
      </>
    );
  }
);

export default TicketAttachments;
